<template>
  <!-- PricePro filter DRAWER (UAT). Rendered as an inline flex panel so the parent can
       slide it out and PUSH the grid over (no overlap), mirroring Ad Planner / TPR Search.
       All filter controls are shown at once. Filters are staged locally and only run
       against the backend when the user clicks Apply (the keyword search stays live). -->
  <div class="pp-filter-panel" :style="panelStyle">
    <div class="pp-filter-header d-flex align-center px-4 py-3">
      <v-icon small class="mr-2">mdi-filter-variant</v-icon>
      <span class="text-subtitle-1 font-weight-medium">Filters</span>
      <v-spacer />
      <v-btn icon small aria-label="Close filters" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-divider />

    <div class="pp-filter-body flex-grow-1 px-4 py-3">
      <!-- Promo Category -->
      <v-autocomplete
        v-model="params.promo_category_name"
        label="Promo Category"
        :items="sortedPromoCat"
        item-text="name"
        item-value="id"
        multiple small-chips deletable-chips hide-details clearable outlined dense
        class="mb-4"
        @change="handlePromoCatChange"
        @click:clear="removeFilter('promo_category_name', true)"
      />

      <!-- Department -->
      <v-autocomplete
        v-model="params.department"
        label="Department"
        :items="sortedDepartments"
        item-text="name"
        item-value="id"
        multiple small-chips deletable-chips hide-details clearable outlined dense
        class="mb-4"
        @change="handleDepartmentChange"
        @click:clear="removeFilter('department', true)"
      />

      <!-- Category -->
      <v-autocomplete
        v-model="params.category"
        label="Category"
        :items="sortedCategories"
        item-text="name"
        item-value="id"
        multiple small-chips deletable-chips hide-details clearable outlined dense
        class="mb-4"
        @change="handleCategoryChange"
        @click:clear="removeFilter('category', true)"
      />

      <!-- Subcategory (AWG only) -->
      <v-autocomplete
        v-if="$auth.tenant === 'awg'"
        v-model="params.subcategory"
        label="Subcategory"
        :items="sortedSubcategories"
        item-text="name"
        item-value="id"
        multiple small-chips deletable-chips hide-details clearable outlined dense
        class="mb-4"
        @change="handleSubcategoryChange"
        @click:clear="removeFilter('subcategory', true)"
      />

      <!-- Vendor -->
      <v-autocomplete
        v-model="params.vendor"
        label="Vendor"
        :items="sortedVendors"
        item-text="name"
        item-value="id"
        multiple small-chips deletable-chips hide-details clearable outlined dense
        class="mb-4"
        @change="handleVendorChange"
        @click:clear="removeFilter('vendor', true)"
      />

      <!-- Private Label AMAPs (ARG only) -->
      <v-autocomplete
        v-if="$auth.tenant === 'alliance-retail-group'"
        v-model="params.private_label_indicator"
        label="Private Label AMAPs"
        :items="[{ text: 'Only Include Private Label AMAPs', value: true }, { text: 'Exclude Private Label AMAPs', value: false }]"
        item-text="text" item-value="value"
        hide-details clearable outlined dense
        class="mb-4"
        @change="handleIsPrivateLabelChange"
        @click:clear="removeFilter('private_label_indicator', true)"
      >
        <template #selection="{ item }"><span>{{ item.text }}</span></template>
      </v-autocomplete>

      <!-- VMC Items (ARG, NON PERISHABLE only) -->
      <v-autocomplete
        v-if="$auth.tenant === 'alliance-retail-group' && batch_type === 'NON PERISHABLE'"
        v-model="params.vmc_only"
        label="VMC Items"
        :items="[{ text: 'Only Include VMC Items', value: true }, { text: 'Exclude VMC Items', value: false }]"
        item-text="text" item-value="value"
        hide-details clearable outlined dense
        class="mb-4"
        @change="handleVmcOnlyChange"
        @click:clear="removeFilter('vmc_only', true)"
      >
        <template #selection="{ item }"><span>{{ item.text }}</span></template>
      </v-autocomplete>

      <!-- Linked Group -->
      <v-textarea
        v-model="params.linked_group"
        label="Linked Group"
        background-color="input"
        hide-details="auto" dense outlined auto-grow rows="1" clearable
        hint="Press 'Enter' to add multiple search criteria."
        class="mb-4"
        @keydown="handleKeydown($event, 'linked_group')"        @click:clear="removeFilter('linked_group', true)"
      />

      <!-- Compete/ePay Filter (ARG only) -->
      <v-autocomplete
        v-if="$auth.tenant === 'alliance-retail-group'"
        v-model="params.filter_compete_epay"
        label="Compete/ePay Filter"
        :items="[{ text: 'Exclude Compete/ePay', value: false }, { text: 'Only Include Compete/ePay', value: true }]"
        item-text="text" item-value="value"
        hide-details clearable outlined dense
        class="mb-4"
        @change="handleFilterCompeteEpayChange"
        @click:clear="removeFilter('filter_compete_epay', true)"
      >
        <template #selection="{ item }"><span>{{ item.text }}</span></template>
      </v-autocomplete>

      <!-- Item ID -->
      <v-textarea
        v-model="params.item_id"
        label="Item ID"
        background-color="input"
        hide-details="auto" dense outlined auto-grow rows="1" clearable
        hint="Press 'Enter' to add multiple search criteria."
        class="mb-4"
        @keydown="handleKeydown($event, 'item_id')"        @click:clear="removeFilter('item_id', true)"
      />

      <!-- UPC -->
      <v-textarea
        v-model="params.upc"
        label="UPC"
        background-color="input"
        hide-details="auto" dense outlined auto-grow rows="1" clearable
        hint="Press 'Enter' to add multiple search criteria."
        class="mb-4"
        @keydown="handleKeydown($event, 'upc')"        @click:clear="removeFilter('upc', true)"
      />

      <!-- Order Book Index (ARG only) -->
      <v-textarea
        v-if="$auth.tenant === 'alliance-retail-group'"
        v-model="params.order_book_index"
        label="Order Book Index"
        background-color="input"
        hide-details="auto" dense outlined auto-grow rows="1" clearable
        hint="Press 'Enter' to add multiple search criteria."
        class="mb-4"
        @keydown="handleKeydown($event, 'order_book_index')"        @click:clear="removeFilter('order_book_index', true)"
      />

      <!-- Description -->
      <v-textarea
        v-model="params.description"
        label="Description"
        background-color="input"
        hide-details="auto" dense outlined auto-grow rows="1" clearable
        class="mb-4"
        @keydown="handleKeydown($event, 'description')"        @click:clear="removeFilter('description', true)"
      />

      <!-- Notes -->
      <v-textarea
        v-model="params.notes"
        label="Notes"
        background-color="input"
        hide-details="auto" dense outlined auto-grow rows="1" clearable
        class="mb-4"
        @keydown="handleKeydown($event, 'notes')"        @click:clear="removeFilter('notes', true)"
      />

      <!-- Shippers/Pallets Filter -->
      <v-autocomplete
        v-model="params.is_shipper"
        label="Shippers/Pallets Filter"
        :items="[{ text: 'Exclude Shippers/Pallets', value: false }, { text: 'Only Include Shippers/Pallets', value: true }]"
        item-text="text" item-value="value"
        hide-details clearable outlined dense
        class="mb-4"
        @change="handleIsShipperChange"
        @click:clear="removeFilter('is_shipper', true)"
      >
        <template #selection="{ item }"><span>{{ item.text }}</span></template>
      </v-autocomplete>

      <!-- Top Seller Filter (ARG only) -->
      <v-autocomplete
        v-if="$auth.tenant === 'alliance-retail-group'"
        v-model="params.top_sellers"
        label="Top Seller Filter"
        :items="[{ text: 'Exclude Top Sellers', value: false }, { text: 'Only Include Top Sellers', value: true }]"
        item-text="text" item-value="value"
        hide-details clearable outlined dense
        class="mb-4"
        @change="handleTopSellersChange"
        @click:clear="removeFilter('top_sellers', true)"
      >
        <template #selection="{ item }"><span>{{ item.text }}</span></template>
      </v-autocomplete>

      <!-- Changed Items Filter (ARG only) -->
      <v-autocomplete
        v-if="$auth.tenant === 'alliance-retail-group'"
        v-model="params.changed_items"
        label="Changed Items Filter"
        :items="[{ text: 'Only Changed Items', value: true }, { text: 'Only Unchanged Items', value: false }]"
        item-text="text" item-value="value"
        hide-details clearable outlined dense
        class="mb-4"
        @change="handleChangedItemsChange"
        @click:clear="removeFilter('changed_items', true)"
      >
        <template #selection="{ item }"><span>{{ item.text }}</span></template>
      </v-autocomplete>
    </div>

    <v-divider />
    <div class="pp-filter-footer d-flex align-center px-4 py-3">
      <v-btn text small color="primary" :disabled="activeFilters.length === 0" @click="removeAllFilters">
        Clear all
      </v-btn>
      <v-spacer />
      <v-btn small color="primary" depressed @click="applyFilters">Apply</v-btn>
    </div>
  </div>
</template>
<script>

import { userAccess } from '@/mixins/user-access'

export default {
  name: 'SearchFilters',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    categories: Array,
    subcategories: Array,
    departments: Array,
    promoCategories: Array,
    vendors: Array,
    batch_type: String,
    // Fixed pixel height so the drawer matches the grid and its body scrolls
    // internally instead of growing the page when there are many filters.
    panelHeight: {
      type: [Number, String],
      default: null
    }
  },
  mixins: [userAccess],
  data() {
    return {
      selectMenu: false,
      selectedFilters: [],
      filters: [
        { name: 'Promo Category', key: 'promo_category_name', color: 'cyan darken-1' },
        { name: 'Department', key: 'department', type: 'text' },
        { name: 'Category', key: 'category', color: 'amber darken-2' },
        ...(this.$auth.tenant == 'awg'
          ? [{ name: 'Subcategory', key: 'subcategory' }]
          : []),
        { name: 'Vendor', key: 'vendor', color: 'amber darken-2' },
        ...(this.$auth.tenant === 'alliance-retail-group'
          ? [{ name: 'Private Label AMAPs', key: 'private_label_indicator' }]
          : []),
        ...(this.$auth.tenant === 'alliance-retail-group' && this.batch_type == 'NON PERISHABLE'
          ? [{ name: 'VMC Items', key: 'vmc_only' }]
          : []),
        { name: 'Linked Group', key: 'linked_group', color: 'cyan darken-1' },
          ...(this.$auth.tenant === 'alliance-retail-group'
          ? [{ name: 'Compete/ePay Filter', key: 'filter_compete_epay', color: 'cyan darken-2' }]
          : []),
        { name: 'Item ID', key: 'item_id', type: 'text' },
        { name: 'UPC', key: 'upc', type: 'text' },
        ...(this.$auth.tenant === 'alliance-retail-group'
          ? [{ name: 'Order Book Index', key: 'order_book_index', color: 'amber darken-2' }]
          : []),
        { name: 'Description', key: 'description', color: 'red' },
        { name: 'Notes', key: 'notes', color: 'red' },
        { name: 'Shippers/Pallets Filter', key: 'is_shipper', color: 'amber darken-2' },
        ...(this.$auth.tenant === 'alliance-retail-group'
          ? [{ name: 'Top Seller Filter', key: 'top_sellers', color: 'amber darken-2' }]
          : []),
        ...(this.$auth.tenant === 'alliance-retail-group'
          ? [{ name: 'Changed Items', key: 'changed_items', color: 'teal darken-1' }]
          : []),
      ],
      params: {
        department: null,
        category: null,
        subcategory: null,
        linked_group: null,
        item_id: null,
        upc: null,
        description: null,
        is_shipper: null,
        notes: null,
        obxi: null,
        order_book_index: null,
        promo_category_name: null,
        private_label_indicator: null,
        filter_compete_epay: null,
        vendor: null,
        vmc_only: null,
        top_sellers: null,
        changed_items: null,
      },
    };
  },
  computed: {
    panelStyle() {
      return this.panelHeight ? { height: `${this.panelHeight}px` } : { height: '100%' };
    },
    availableFilters() {
      return this.filters.filter(f => (!f.tenants || f.tenants.includes(this.$auth.tenant)));
    },
    activeFilters() {
      return Object.keys(this.params).filter(this.isActiveValue);
    },
    sortedCategories() {
      let categories = [...this.categories.map(cat => {
        if (typeof cat === 'string') {
          return { name: cat, disabled: false };
        }
        cat.disabled = cat.disabled || false;
        return cat;
      })];

      categories = this.sortByKey(categories, 'name');

      return categories;
    },
    sortedSubcategories() {
      let subcategories = [...this.subcategories.map(scat => {
        if (typeof scat === 'string') {
          return { name: scat, disabled: false };
        }
        scat.disabled = scat.disabled || false;
        return scat;
      })];

      subcategories = this.sortByKey(subcategories, 'name');

      return subcategories;
    },
    sortedDepartments() {
      let departments = [...this.departments.map(dep => {
        if (typeof dep === 'string') {
          return { name: dep, disabled: false };
        }
        dep.disabled = dep.disabled || false;
        return dep;
      })];

      departments = this.sortByKey(departments, 'name');
      return departments;
    },
    sortedPromoCat() {
      let promos = [...this.promoCategories.map(dep => {
        if (typeof dep === 'string') {
          return { name: dep, disabled: false };
        }
        dep.disabled = dep.disabled || false;
        return dep;
      })];

      promos = this.sortByKey(promos, 'name');
      return promos;
    },
    sortedVendors() {
      let vendors = [...this.vendors.map(vendor => {
        if (typeof vendor === 'string') {
          return { name: vendor, disabled: false };
        }
        vendor.disabled = vendor.disabled || false;
        return vendor;
      })];

      vendors = this.sortByKey(vendors, 'name');
      return vendors;
    }
  },
  methods: {
    // Filters are staged locally; they only query the backend when Apply is clicked.
    applyFilters() {
      this.triggerSearch();
      this.$emit('close');
    },
    handleKeydown(event, key) {
      if (event.key === 'Enter' && (key === 'notes' || key === 'description')) {
        event.preventDefault();
      }
    },
    sortByKey(array, key) {
      return array.sort((a, b) => a[key].localeCompare(b[key]));
    },
    handleCategoryChange(selectedCategories) {
        this.params.category = selectedCategories;
        if (selectedCategories.length === 0) {
            this.params.subcategory = null;
            this.removeFilter('subcategory', true);
        }
        if (selectedCategories.length > 0 && !this.selectedFilters.includes('category')) {
            this.selectedFilters.push('category');
        } else if (selectedCategories.length === 0) {
            this.removeFilter('category', true);
        }    },
    handleSubcategoryChange(selectedSubcategories) {
      this.params.subcategory = selectedSubcategories;
      if (selectedSubcategories.length > 0 && !this.selectedFilters.includes('subcategory')) {
        this.selectedFilters.push('subcategory');
      } else if (selectedSubcategories.length === 0) {
        this.removeFilter('subcategory', true);
      }    },
    handleDepartmentChange(selectedDepartments) {
      this.params.department = selectedDepartments;
      if (selectedDepartments.length > 0 && !this.selectedFilters.includes('department')) {
        this.selectedFilters.push('department');
      } else if (selectedDepartments.length === 0) {
        this.removeFilter('department', true);
      }    },
    handlePromoCatChange(selectedPromoCats) {
      this.params.promo_category_name = selectedPromoCats;
      if (selectedPromoCats.length > 0 && !this.selectedFilters.includes('promo_category_name')) {
        this.selectedFilters.push('promo_category_name');
      } else if (selectedPromoCats.length === 0) {
        this.removeFilter('promo_category_name', true);
      }    },
    handleVendorChange(selectedVendors) {
      this.params.vendor = selectedVendors;
      if (selectedVendors.length > 0 && !this.selectedFilters.includes('vendor')) {
        this.selectedFilters.push('vendor');
      } else if (selectedVendors.length === 0) {
        this.removeFilter('vendor', true);
      }    },
    handleIsShipperChange(value) {
      if (value === null) {
        this.removeFilter('is_shipper', true);
      } else {
        if (!this.selectedFilters.includes('is_shipper')) {
          this.selectedFilters.push('is_shipper');
        }
        this.params.is_shipper = value;      }
    },
    handleTopSellersChange(value) {
      if (value === null) {
        this.removeFilter('top_sellers', true);
      } else {
        if (!this.selectedFilters.includes('top_sellers')) {
          this.selectedFilters.push('top_sellers');
        }
        this.params.top_sellers = value;      }
    },
    handleChangedItemsChange(value) {
      if (value === null) {
        this.removeFilter('changed_items', true);
      } else {
        if (!this.selectedFilters.includes('changed_items')) {
          this.selectedFilters.push('changed_items');
        }
        this.params.changed_items = value;
      }
    },
    handleIsPrivateLabelChange(value) {
      if (value === null) {
        this.removeFilter('private_label_indicator', true);
      } else {
        if (!this.selectedFilters.includes('private_label_indicator')) {
          this.selectedFilters.push('private_label_indicator');
        }
        this.params.private_label_indicator = value;      }
    },
    handleFilterCompeteEpayChange(value) {
      if (value === null) {
        this.removeFilter('filter_compete_epay', true);
      } else {
        if (!this.selectedFilters.includes('filter_compete_epay')) {
          this.selectedFilters.push('filter_compete_epay');
        }
        this.params.filter_compete_epay = value;      }
    },
    handleVmcOnlyChange(value) {
      if (value === null) {
        this.removeFilter('vmc_only', true);
      } else {
        if (!this.selectedFilters.includes('vmc_only')) {
          this.selectedFilters.push('vmc_only');
        }
        this.params.vmc_only = value;      }
    },
    removeFilter(key, clearValue = false) {
      const index = this.selectedFilters.indexOf(key);
      if (index > -1) {
        this.selectedFilters.splice(index, 1);
      }
      if (clearValue) {
        this.params[key] = null;
      }
    },
    removeAllFilters() {
      // Clear all filters in params by setting them to null
      Object.keys(this.params).forEach(key => {
        this.params[key] = null;
      });

      // Clear the selectedFilters array
      this.selectedFilters = [];

      // Clear any stored filters in sessionStorage (the grid updates on next Apply)
      sessionStorage.removeItem('search_filters');
    },
    isActiveValue(key) {
      const value = this.params[key];
      if (value === null || value === undefined || value === '') return false;
      return Array.isArray(value) ? value.length > 0 : true;
    },
    getFilterText(item) {
      return `${item.name}`;
    },
    triggerSearch() {
      this.$emit('input', { ...this.params });
      this.updateSearchFilters();
      if (this.activeFilters.length) {
        this.$gtag.event('filter_apply', { filter_types: [...this.activeFilters].sort().join(',') });
      }
    },
    updateSearchFilters() {
      const filtersObj = this.activeFilters.reduce((obj, key) => {
        obj[key] = this.params[key];
        return obj;
      }, {});
      if (this.activeFilters.length > 0) {
        sessionStorage.setItem('search_filters', JSON.stringify(filtersObj));
      } else {
        sessionStorage.removeItem('search_filters');
      }
    },
    getFilterLabel(key) {
      const filter = this.filters.find(f => f.key === key);
      return filter ? filter.name : key;
    }
  },
  watch: {
    params: {
      handler() {
        Object.keys(this.params).forEach(key => {
          if (this.params[key] === "") {
            this.params[key] = null;
            this.removeFilter(key);
          }
          else if (this.params[key] !== null && typeof this.params[key] === 'string' && key != 'notes' && key != 'description') {
            var text = this.params[key].split(/[\n,\t,]+/);
            this.params[key] = text
          }
        });
      },
      deep: true
    }
  },
  created(){
    if (sessionStorage.getItem('search_filters')) {
      const stored = JSON.parse(sessionStorage.getItem('search_filters'));
      this.params = { ...this.params, ...stored };
      const activeKeys = Object.keys(stored).filter(key => Boolean(stored[key]));
      if (activeKeys.length > 0) this.selectedFilters.push(...activeKeys);
    }
  },
}
</script>
<style scoped>
/* Use plain (non-!important) flex so v-show's inline `display:none` can override it
   when the drawer is closed — the .d-flex utility would win over v-show and never hide. */
.pp-filter-panel {
  display: flex;
  flex-direction: column;
  width: 360px;
  background: #fff;
  border-left: 1px solid #e0e0e0;
}
.pp-filter-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.pp-filter-header {
  background: #fafafa;
}
.pp-filter-footer {
  background: #fafafa;
}
</style>
